// src/pages/Dashboard.tsx
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { orderBy, limit } from "firebase/firestore";
import { useAuth } from "@/lib/auth";
import { useDocumentTitle } from "@/lib/useDocumentTitle";
import {
  subscribeToTickets, getTicketStats,
  getDashboardOpsStats, DashboardOpsStats,
  getDeflectionStats, DeflectionStats,
} from "@/lib/firestore";
import { Ticket } from "@/types";
import { StatusBadge, PriorityBadge, AiTriageBadge, SectionHeader } from "@/components/shared";
import { formatDistanceToNow } from "date-fns";

function formatDuration(ms: number | null): string {
  if (ms === null) return "—";
  const h = ms / (1000 * 60 * 60);
  if (h < 1) return `${Math.max(1, Math.round(h * 60))}m`;
  if (h < 48) return `${h.toFixed(1)}h`;
  return `${(h / 24).toFixed(1)}d`;
}

export default function Dashboard() {
  useDocumentTitle("Dashboard");
  const { helpDeskUser, firebaseUser } = useAuth();
  const navigate = useNavigate();

  // Google-SSO users often arrive without firstName/lastName populated —
  // their Firestore doc has displayName only. Fall through to displayName,
  // then to whatever Firebase Auth gave us, then to "there".
  const greetingName =
    helpDeskUser?.firstName?.trim() ||
    helpDeskUser?.displayName?.split(" ")[0] ||
    firebaseUser?.displayName?.split(" ")[0] ||
    "there";
  const [stats, setStats] = useState({ open: 0, inProgress: 0, resolvedToday: 0, critical: 0 });
  const [ops, setOps] = useState<DashboardOpsStats | null>(null);
  const [deflections, setDeflections] = useState<DeflectionStats | null>(null);
  const [recentTickets, setRecentTickets] = useState<Ticket[]>([]);

  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";

  useEffect(() => {
    const refresh = () => {
      getTicketStats().then(setStats).catch((e) => console.error("getTicketStats failed:", e));
      getDashboardOpsStats().then(setOps).catch((e) => console.error("getDashboardOpsStats failed:", e));
      getDeflectionStats().then(setDeflections).catch((e) => console.error("getDeflectionStats failed:", e));
    };
    refresh();
    // Re-fetch counters whenever any ticket changes — keeps Open/In Progress/
    // Resolved Today/P1 in sync after creates, status changes, or resolves.
    const unsub = subscribeToTickets(
      [orderBy("updatedAt", "desc"), limit(6)],
      (tickets) => { setRecentTickets(tickets); refresh(); }
    );
    return unsub;
  }, []);

  // Stat cards. `--blue-t` is the always-readable text variant of the
  // brand color, so the value stays AA-contrast even when admins set
  // a light brand color (orange, yellow, etc.) via Branding settings.
  const PRIMARY_STATS = [
    { label: "Open Tickets",    val: stats.open,          color: "var(--blue-t)",   sub: "awaiting action",        href: "/tickets?status=open" },
    { label: "In Progress",     val: stats.inProgress,    color: "var(--orange-t)", sub: "being worked",            href: "/tickets?status=in_progress" },
    { label: "Resolved Today",  val: stats.resolvedToday, color: "var(--green-t)",  sub: "closed today",            href: "/tickets?status=resolved&since=today" },
    { label: "P1 Critical",     val: stats.critical,      color: "var(--red-t)",    sub: "needs immediate action", href: "/tickets?status=open&priority=P1" },
  ];
  const OPS_STATS = [
    {
      label: "Avg Resolution",
      val: formatDuration(ops?.avgResolutionMs ?? null),
      color: "var(--green-t)",
      sub: ops ? `${ops.resolvedLast30} resolved · last 30 days` : "loading…",
      loading: !ops,
    },
    {
      label: "SLA Compliance",
      val: ops?.slaCompliancePct !== null && ops?.slaCompliancePct !== undefined ? `${ops.slaCompliancePct}%` : "—",
      color: ops?.slaCompliancePct !== null && ops?.slaCompliancePct !== undefined && ops.slaCompliancePct < 90 ? "var(--orange-t)" : "var(--green-t)",
      sub: ops ? `${ops.totalLast30} tickets · last 30 days` : "loading…",
      loading: !ops,
    },
    {
      label: "Avg Queue Age",
      val: formatDuration(ops?.avgOpenAgeMs ?? null),
      color: "var(--blue-t)",
      sub: "currently open tickets",
      loading: !ops,
    },
  ];
  const DEFLECTION_STATS = [
    {
      label: "Tickets Deflected",
      val: deflections ? String(deflections.resolvedLast30) : "—",
      color: "var(--gem-t)",
      sub: deflections ? `${deflections.byChannel.kb} via KB · ${deflections.byChannel.aiChat} via AI` : "loading…",
      loading: !deflections,
    },
    {
      label: "Deflection Rate",
      val: deflections?.deflectionRate !== null && deflections?.deflectionRate !== undefined ? `${deflections.deflectionRate}%` : "—",
      color: "var(--gem-t)",
      sub: deflections ? `${deflections.resolvedLast30} resolved vs ${deflections.ticketsCreatedLast30} tickets` : "loading…",
      loading: !deflections,
    },
    {
      label: "Tickets Created",
      val: deflections ? String(deflections.ticketsCreatedLast30) : "—",
      color: "var(--orange-t)",
      sub: "last 30 days",
      loading: !deflections,
    },
  ];

  return (
    <div style={{ overflowY: "auto", flex: 1, padding: 20 }}>
      <h1 style={{ marginBottom: 3 }}>
        {greeting}, {greetingName}
        <span aria-hidden="true"> 👋</span>
      </h1>
      <p style={{ color: "var(--text2)", fontSize: 13, marginBottom: 20 }}>Your AI-triaged IT queue — here's what needs attention.</p>

      {/* Key Metrics */}
      <h2 style={{ fontSize: 13, fontWeight: 600, color: "var(--text2)", textTransform: "uppercase", letterSpacing: .5, marginBottom: 8 }}>Key Metrics</h2>
      <div role="list" style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 10, marginBottom: 20 }}>
        {PRIMARY_STATS.map((s) => (
          <article
            key={s.label} role="listitem"
            aria-label={`${s.label}: ${s.val}, ${s.sub}`}
            tabIndex={0}
            className="card card-body row-button"
            onClick={() => navigate(s.href)}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); navigate(s.href); } }}
            style={{ padding: 14, cursor: "pointer", transition: "transform 0.1s, box-shadow 0.1s" }}
            onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "var(--shadow-md)"; e.currentTarget.style.transform = "translateY(-1px)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.boxShadow = ""; e.currentTarget.style.transform = ""; }}
          >
            <div style={{ fontSize: 12, color: "var(--text2)", marginBottom: 5, fontWeight: 500 }}>{s.label}</div>
            <div aria-hidden="true" style={{ fontSize: 28, fontWeight: 700, letterSpacing: -1, color: s.color }}>{s.val}</div>
            <div style={{ fontSize: 11, color: "var(--text2)", marginTop: 3 }}>{s.sub}</div>
          </article>
        ))}
      </div>

      {/* Operational performance — last 30 days */}
      <h2 style={{ fontSize: 13, fontWeight: 600, color: "var(--text2)", textTransform: "uppercase", letterSpacing: .5, marginBottom: 8 }}>Operational Performance</h2>
      <div role="list" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 20 }}>
        {OPS_STATS.map((s) => (
          <article
            key={s.label} role="listitem"
            aria-label={`${s.label}: ${s.val}, ${s.sub}`}
            aria-busy={s.loading}
            className="card card-body"
            style={{ padding: 14, borderLeft: `3px solid ${s.color}` }}
          >
            <div style={{ fontSize: 12, color: "var(--text2)", marginBottom: 5, fontWeight: 500 }}>{s.label}</div>
            <div aria-hidden="true" style={{ fontSize: 24, fontWeight: 700, letterSpacing: -.5, color: s.color }}>{s.val}</div>
            <div style={{ fontSize: 11, color: "var(--text2)", marginTop: 3 }}>{s.sub}</div>
          </article>
        ))}
      </div>

      {/* Self-service deflection — last 30 days */}
      <h2 style={{ fontSize: 13, fontWeight: 600, color: "var(--text2)", textTransform: "uppercase", letterSpacing: .5, marginBottom: 8 }}>Self-Service Deflection</h2>
      <div role="list" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 20 }}>
        {DEFLECTION_STATS.map((s) => (
          <article
            key={s.label} role="listitem"
            aria-label={`${s.label}: ${s.val}, ${s.sub}`}
            aria-busy={s.loading}
            className="card card-body"
            style={{ padding: 14, borderLeft: `3px solid ${s.color}` }}
          >
            <div style={{ fontSize: 12, color: "var(--text2)", marginBottom: 5, fontWeight: 500 }}>{s.label}</div>
            <div aria-hidden="true" style={{ fontSize: 24, fontWeight: 700, letterSpacing: -.5, color: s.color }}>{s.val}</div>
            <div style={{ fontSize: 11, color: "var(--text2)", marginTop: 3 }}>{s.sub}</div>
          </article>
        ))}
      </div>

      {/* Recent tickets */}
      <SectionHeader title="Recent Tickets" action={{ label: "See all →", onClick: () => navigate("/tickets") }} />
      <TicketTable tickets={recentTickets} onSelect={(id) => navigate(`/tickets/${id}`)} />
    </div>
  );
}

export function TicketTable({ tickets, onSelect }: { tickets: Ticket[]; onSelect: (id: string) => void }) {
  if (!tickets.length) {
    return (
      <div className="card" style={{ padding: "32px 16px", textAlign: "center", color: "var(--text2)", fontSize: 13 }}>
        No tickets found
      </div>
    );
  }

  return (
    <div className="card" style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <caption className="sr-only">Recent tickets — press Enter on a row to open it</caption>
        <thead>
          <tr style={{ background: "var(--gray)" }}>
            {["ID","Title","Status","Priority","Triage","Updated"].map((h) => (
              <th key={h} scope="col" style={{ fontSize: 11, fontWeight: 600, color: "var(--text2)", textTransform: "uppercase", letterSpacing: .3, padding: "9px 12px", textAlign: "left", borderBottom: "0.5px solid var(--border)" }}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {tickets.map((t, i) => (
            <tr
              key={t.id}
              role="button"
              tabIndex={0}
              aria-label={`Ticket ${t.id.slice(0, 8).toUpperCase()}: ${t.title}, ${t.status}${t.priority ? ", priority " + t.priority : ""}`}
              onClick={() => onSelect(t.id)}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSelect(t.id); } }}
              className="row-button"
              style={{ borderBottom: i < tickets.length - 1 ? "0.5px solid var(--border)" : "none" }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "var(--gray)")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
            >
              <td style={{ padding: "10px 12px" }}>
                <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--text2)", background: "var(--gray)", padding: "2px 6px", borderRadius: 4 }}>
                  {t.id.slice(0, 8).toUpperCase()}
                </span>
              </td>
              <td style={{ padding: "10px 12px", maxWidth: 220 }}>
                <div style={{ fontSize: 13, fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{t.title}</div>
                <div style={{ fontSize: 11, color: "var(--text2)" }}>{t.submitterName ?? t.submitterEmail}</div>
              </td>
              <td style={{ padding: "10px 12px" }}><StatusBadge status={t.status} /></td>
              <td style={{ padding: "10px 12px" }}>{t.priority ? <PriorityBadge priority={t.priority} /> : <span style={{ color: "var(--text3)", fontSize: 12 }}>Triaging…</span>}</td>
              <td style={{ padding: "10px 12px" }}>{t.triagedAt ? <AiTriageBadge /> : <span style={{ fontSize: 11, color: "var(--text3)" }}>Pending</span>}</td>
              <td style={{ padding: "10px 12px", color: "var(--text2)", fontSize: 12 }}>
                {t.updatedAt ? formatDistanceToNow(t.updatedAt.toDate(), { addSuffix: true }) : "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
